import React from 'react'
import { Show, TextField, DateField } from '@refinedev/antd'
import { useList, useShow } from '@refinedev/core'
import { Link } from 'react-router'
import { Button, Table, Typography } from 'antd'
import type { Tables } from '@muaa/shared'
import { brand } from '../../theme/brand'
import { Pill } from '../../theme/listkit'
import { CalendarDays } from '../../components/icons'

type Event = Tables<'events'>
type EventSession = Tables<'event_sessions'>

const { Title } = Typography

export function EventShow() {
  // LIVE opt-in (the global liveMode stays 'off' — see App.tsx): a single-record
  // subscription so edits made in another tab or by another admin refresh this
  // page in place — one row per refetch, cheap and bounded.
  const { query } = useShow<Event>({ liveMode: 'auto' })
  const { data, isLoading } = query
  const record = data?.data

  // Sub-events (children) of THIS event — bounded fetch (an event's agenda is a
  // handful of sessions; 50 is generous headroom) + staleTime so revisits don't
  // refire. LIVE via liveMode:'auto' so agenda changes (a session added,
  // removed or reordered elsewhere) land here without a manual refresh — the
  // refetch is this same event-filtered bounded read, so opting in is cheap.
  const { result: sessionsRes } = useList<EventSession>({
    resource: 'event_sessions',
    filters: record?.id ? [{ field: 'event_id', operator: 'eq', value: record.id }] : [],
    sorters: [{ field: 'sort_order', order: 'asc' }],
    pagination: { pageSize: 50 },
    liveMode: 'auto',
    queryOptions: { enabled: Boolean(record?.id), staleTime: 300_000 },
  })
  const sessions = sessionsRes?.data ?? []

  return (
    <Show isLoading={isLoading}>
      <Title level={5}>Title</Title>
      <TextField value={record?.title} />

      <Title level={5}>Subtitle</Title>
      <TextField value={record?.subtitle ?? '—'} />

      <Title level={5}>Start Date</Title>
      {record?.start_date ? <DateField value={record.start_date} format="MMM D, YYYY" /> : <TextField value="—" />}

      <Title level={5}>End Date</Title>
      {record?.end_date ? <DateField value={record.end_date} format="MMM D, YYYY" /> : <TextField value="—" />}

      <Title level={5}>Location</Title>
      <TextField value={record?.location ?? '—'} />

      <Title level={5}>Description</Title>
      <TextField value={record?.description ?? '—'} />

      <Title level={5}>Cheddar Up Ticket URL</Title>
      <TextField value={record?.cheddarup_url ?? '— (no Get Tickets button in the app)'} />

      <Title level={5}>Home Hero</Title>
      <TextField
        value={record?.hero_featured ? 'Pinned as the Home hero' : 'Not pinned (latest event shows by default)'}
      />

      {/* ── Sub-events (the agenda inside this event) ─────────────────────── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 24,
        }}
      >
        <Title level={5} style={{ margin: 0 }}>
          Sub-events
        </Title>
        {record?.id ? (
          <Link to={`/event_sessions/create?event_id=${record.id}`}>
            <Button type="primary" size="small" icon={<CalendarDays size={14} />}>
              Add sub-event
            </Button>
          </Link>
        ) : null}
      </div>
      {sessions.length > 0 ? (
        <Table<EventSession>
          dataSource={sessions}
          rowKey="id"
          size="small"
          pagination={false}
          style={{ marginTop: 12 }}
        >
          <Table.Column<EventSession>
            dataIndex="title"
            title="Title"
            render={(title: string, row: EventSession) => (
              <Link
                to={`/event_sessions/edit/${row.id}`}
                style={{ fontFamily: brand.fDisplay, fontWeight: 700, color: brand.ink }}
              >
                {title}
              </Link>
            )}
          />
          <Table.Column<EventSession>
            dataIndex="day"
            title="Day"
            render={(day: string | null) =>
              day ? <Pill tone="gold">{day}</Pill> : <span style={{ color: brand.sub }}>—</span>
            }
          />
          <Table.Column<EventSession>
            dataIndex="start_time"
            title="Start Time"
            render={(t: string | null) =>
              t ? (
                <span style={{ fontFamily: brand.fSans, fontWeight: 600, color: brand.ink }}>{t}</span>
              ) : (
                <span style={{ color: brand.sub }}>—</span>
              )
            }
          />
          <Table.Column<EventSession>
            dataIndex="sort_order"
            title="Sort Order"
            render={(v: number | null) =>
              v != null ? (
                <span style={{ fontFamily: brand.fSans, fontWeight: 600, color: brand.sub }}>{String(v)}</span>
              ) : (
                <span style={{ color: brand.sub }}>—</span>
              )
            }
          />
        </Table>
      ) : (
        <div style={{ marginTop: 12, fontFamily: brand.fSans, fontSize: 13, color: brand.sub }}>
          No sub-events yet — the RSVP flow will show a simple RSVP with no agenda checklist
        </div>
      )}
    </Show>
  )
}
